import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { ApplyDropdown } from './ApplyDropdown';
import { useTheme } from '../../context/ThemeContext';
import { LANDING_IMAGES } from '../../lib/landingImages';

export const HeroV2 = ({ onApplySelect }) => {
  const { t } = useTranslation();
  // Yalnız aktif temanın hero görseli DOM'a girer: gizli <img loading="eager"> da indiriliyordu
  // (ilk açılışta iki tema görseli birden). Masaüstü/mobil aynı dosya, tek indirme.
  const { theme } = useTheme();
  const dark = theme === 'dark';

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16" data-testid="v2-hero">
      {/* Background: navy base + neon orbs + grid */}
      <div className="absolute inset-0 bg-background">
        <div className="absolute top-[-15%] left-[-10%] w-[700px] h-[700px] rounded-full blur-[140px] bg-fuchsia-600/35 animate-neon-pulse dark:lg:opacity-30" />
        <div className="absolute bottom-[-20%] right-[-15%] w-[800px] h-[800px] rounded-full blur-[160px] bg-orange-600/25 animate-neon-pulse dark:lg:opacity-20" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/3 right-1/4 w-[450px] h-[450px] rounded-full blur-[120px] bg-indigo-700/30 dark:lg:opacity-20" />
      </div>
      <div className="absolute inset-0 neon-grid-bg opacity-40 dark:lg:opacity-0" />

      {/* Dark + desktop: real branded hero photo background */}
      {dark && (
        <img
          {...LANDING_IMAGES.heroDark}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          className="hidden lg:dark:block absolute inset-0 w-full h-full object-cover object-right pointer-events-none"
          data-testid="v2-hero-bg-image"
        />
      )}
      <div className="hidden lg:dark:block absolute inset-0 pointer-events-none bg-gradient-to-r from-background via-background/85 to-background/40" />
      <div className="hidden lg:dark:block absolute inset-0 pointer-events-none bg-gradient-to-b from-background/30 via-transparent to-background/60" />

      {/* Light + desktop: branded hero photo background */}
      {!dark && (
        <img
          {...LANDING_IMAGES.heroLight}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          className="hidden lg:block dark:lg:hidden absolute inset-0 w-full h-full object-cover object-right pointer-events-none"
          data-testid="v2-hero-bg-image-light"
        />
      )}
      <div className="hidden lg:block dark:lg:hidden absolute inset-0 pointer-events-none bg-gradient-to-r from-background via-background/70 to-transparent" />
      <div className="hidden lg:block dark:lg:hidden absolute inset-0 pointer-events-none bg-gradient-to-b from-background/10 via-transparent to-background/30" />

      {/* Mobile (lg-) backgrounds — different image per theme */}
      {dark && (
        <img
          {...LANDING_IMAGES.heroDark}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          className="hidden dark:block dark:lg:hidden absolute inset-0 w-full h-full object-cover object-center opacity-90 pointer-events-none"
          data-testid="v2-hero-bg-image-mobile-dark"
        />
      )}
      {!dark && (
        <img
          {...LANDING_IMAGES.heroLight}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          className="block lg:hidden dark:hidden absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
          data-testid="v2-hero-bg-image-mobile-light"
        />
      )}
      {/* Mobile readability overlays */}
      <div className="lg:hidden absolute inset-0 pointer-events-none bg-gradient-to-b from-background via-background/60 to-background/40" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 w-full">
        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center font-heading font-black tracking-tight leading-[0.95]"
          data-testid="v2-hero-title"
        >
          <span className="block text-5xl sm:text-7xl lg:text-8xl text-foreground">
            {t('v2_hero_title_line1')}
          </span>
          <span className="block text-5xl sm:text-7xl lg:text-8xl bg-clip-text text-transparent bg-gradient-to-r from-fuchsia-400 via-purple-500 to-fuchsia-600 neon-text-purple my-2">
            {t('v2_hero_title_line2')}
          </span>
          <span className="block text-5xl sm:text-7xl lg:text-8xl text-foreground">
            {t('v2_hero_title_line3')}
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-10 max-w-3xl mx-auto text-center text-base sm:text-lg text-foreground/70 leading-relaxed"
        >
          {t('v2_hero_sub')}
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 flex justify-center"
        >
          <ApplyDropdown onSelect={onApplySelect} size="lg" testIdPrefix="v2-hero-apply" />
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="mt-16 flex justify-center"
        >
          <a
            href="#audiences"
            className="group inline-flex flex-col items-center gap-2 text-foreground/70 dark:text-fuchsia-300 hover:text-foreground dark:hover:text-fuchsia-200 transition-colors text-xs uppercase tracking-widest font-semibold"
          >
            <span className="dark:drop-shadow-[0_0_8px_rgba(176,38,255,0.65)]">
              {t('v2_hero_scroll')}
            </span>
            <ArrowDown className="w-4 h-4 animate-bounce dark:drop-shadow-[0_0_8px_rgba(176,38,255,0.7)]" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
